import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useEffect, useRef, useState } from 'react';

export default function CeramicServiceSection() {
  const videoRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [videoSrc, setVideoSrc] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Lazy load: only inject iframe src when container enters viewport
    const lazyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !videoSrc) {
            setVideoSrc('https://player.vimeo.com/video/1164582278?autoplay=1&loop=1&muted=1&background=1&controls=0');
          }
        });
      },
      { rootMargin: '200px', threshold: 0 }
    );

    lazyObserver.observe(container);
    return () => lazyObserver.disconnect();
  }, [videoSrc]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !videoSrc) return;

    // Audio control: unmute when 50% visible
    const audioObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            videoElement.contentWindow?.postMessage(JSON.stringify({ method: 'setVolume', value: 1 }), '*');
          } else {
            videoElement.contentWindow?.postMessage(JSON.stringify({ method: 'setVolume', value: 0 }), '*');
          }
        });
      },
      { threshold: 0.5 }
    );

    audioObserver.observe(videoElement);
    return () => audioObserver.disconnect();
  }, [videoSrc]);

  const whatsappMessage = encodeURIComponent('Hola, me gustaría consultar sobre Ceramic Coating (ONYX)');
  const whatsappLink = `https://wa.me/18096318952?text=${whatsappMessage}`;

  const features = [
    {
      id: 'ceramic_hardness',
      icon: 'ShieldCheckIcon',
      title: 'DUREZA 10H & N1',
      description: 'Superficie ultra resistente contra rayones y remolinos'
    },
    {
      id: 'ceramic_hydro',
      icon: 'BeakerIcon',
      title: 'REPELENCIA EXTREMA',
      description: 'Efecto hidrofóbico superior: agua y suciedad se deslizan'
    },
    {
      id: 'ceramic_shine',
      icon: 'SparklesIcon',
      title: 'BRILLO ESPEJO',
      description: 'Acabado permanente con profundidad de color intensificada'
    },
    {
      id: 'ceramic_chemical',
      icon: 'BeakerIcon',
      title: 'PROTECCIÓN QUÍMICA',
      description: 'Barrera contra contaminantes, ácidos y clima extremo'
    }
  ];

  const packages = [
    {
      id: 'graphene_pro',
      name: 'Graphene Pro',
      duration: 'LIFETIME',
      durability: 'De por vida',
      description: 'El nivel máximo de protección nanotecnológica',
      features: [
        'Dureza 10H & N1',
        'Repelencia extrema permanente',
        'Brillo espejo de por vida',
        'Descontaminación completa',
        'Corrección de defectos incluida',
        'Curado controlado profesional'
      ],
      highlight: true
    },
    {
      id: 'graphene_coat',
      name: 'Graphene Coat',
      duration: '7 AÑOS',
      durability: '7 años',
      description: 'Protección avanzada con tecnología de grafeno',
      features: [
        'Protección contra químicos y clima',
        'Brillo intenso duradero',
        'Superficie ultra lisa',
        'Descontaminación de pintura',
        'Corrección de defectos',
        'Curado controlado'
      ]
    },
    {
      id: 'quartz_pro',
      name: 'Quartz Pro 9H',
      duration: '5 AÑOS',
      durability: '5 años',
      description: 'Sellado de cuarzo con dureza 9H',
      features: [
        'Dureza 9H certificada',
        'Superficie ultra lisa',
        'Reduce adherencia de contaminantes',
        'Preparación de pintura',
        'Corrección básica incluida'
      ]
    },
    {
      id: 'quartz_plus',
      name: 'Quartz Plus',
      duration: '3 AÑOS',
      durability: '3 años',
      description: 'Protección premium con acabado hidrofóbico',
      features: [
        'Efecto hidrofóbico avanzado',
        'Acabado premium brillante',
        'Protección contra rayones menores',
        'Preparación de superficie'
      ]
    }
  ];

  return (
    <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-black via-card to-black" id="ceramico">
      <div className="max-w-[1600px] mx-auto">
        {/* Section Header */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <div className="order-2 lg:order-1 relative flex justify-center">
            <div className="relative max-w-md w-full" ref={containerRef}>
              {/* Mobile: 16:9, Desktop: 9:16 — fixed aspect ratio prevents layout shift */}
              <div className="aspect-[16/9] md:aspect-[9/16] rounded-[32px] overflow-hidden border-2 border-primary/30 shadow-[0_0_40px_rgba(239,68,68,0.3),0_0_80px_rgba(239,68,68,0.2)] hover:scale-105 transition-transform duration-500 relative">
                {/* Poster placeholder shown until video loads */}
                {!isLoaded && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-gradient-to-br from-black via-zinc-900 to-black">
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center mx-auto mb-3 animate-pulse">
                        <svg className="w-7 h-7 text-primary" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                      <p className="text-white/40 text-xs font-heading tracking-wider">CERAMIC COATING</p>
                    </div>
                  </div>
                )}
                {videoSrc && (
                  <iframe
                    ref={videoRef}
                    src={videoSrc}
                    className="w-full h-full"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title="Ceramic Coating showcase video"
                    onLoad={() => setIsLoaded(true)}
                  />
                )}
              </div>
            </div>
            <div className="absolute -top-8 -left-8 glass-card p-8 rounded-[32px]">
              <div className="text-primary font-heading text-4xl mb-2">10H</div>
              <div className="text-white/80 text-sm font-medium">
                Dureza máxima del recubrimiento
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <span className="text-primary font-heading text-sm tracking-[0.3em] mb-4 block">
              02 / CERAMIC COATING (ONYX)
            </span>
            <h2 className="font-heading text-4xl md:text-6xl text-white mb-6 tracking-tight">
              SELLADO
              <br />
              <span className="text-gradient-red">NANOTECNOLÓGICO PREMIUM</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              Protección líquida que crea una capa permanente de brillo espejo y repelencia superior. Tecnología de grafeno y cuarzo para máxima durabilidad.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="glass-card px-6 py-3 rounded-full">
                <span className="text-primary font-heading text-sm tracking-wider">
                  TECNOLOGÍA DE GRAFENO
                </span>
              </div>
              <div className="glass-card px-6 py-3 rounded-full">
                <span className="text-white/80 font-heading text-sm tracking-wider">
                  BRILLO PERMANENTE
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {features.map((feature) => (
            <div
              key={feature.id}
              className="glass-card p-6 rounded-[24px] hover:bg-white/10 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mb-4">
                <Icon
                  name={feature.icon as any}
                  size={24}
                  variant="outline"
                  className="text-primary"
                />
              </div>
              <h3 className="font-heading text-white text-sm tracking-wider mb-2">
                {feature.title}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Packages */}
        <div>
          <div className="flex flex-col items-center mb-12">
            <div className="w-32 h-32 bg-white/5 rounded-2xl flex items-center justify-center p-4 mb-6">
              <AppImage
                src="/assets/images/onyx_ceramic_logo.png"
                alt="ONYX Ceramic Coating logo"
                className="w-full h-full object-contain"
              />
            </div>
            <h3 className="font-heading text-2xl md:text-3xl text-white text-center tracking-wider">
              PAQUETES DE DURABILIDAD
            </h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`glass-card p-6 rounded-[24px] hover:bg-white/10 transition-all duration-300 relative ${
                  pkg.highlight ? 'border-2 border-primary' : ''
                }`}
              >
                {pkg.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary px-4 py-1 rounded-full">
                    <span className="text-white font-heading text-[10px] tracking-wider">
                      NIVEL MÁXIMO
                    </span>
                  </div>
                )}
                <div className="mb-4">
                  <div className="text-primary font-heading text-2xl mb-1">
                    {pkg.duration}
                  </div>
                  <h4 className="font-heading text-lg text-white mb-2 tracking-wider">
                    {pkg.name}
                  </h4>
                  <p className="text-white/60 text-xs leading-relaxed">
                    {pkg.description}
                  </p>
                </div>
                <ul className="space-y-2">
                  {pkg.features.map((feature, idx) => (
                    <li
                      key={`${pkg.id}_feature_${idx}`}
                      className="flex items-start gap-2 text-white/70 text-xs"
                    >
                      <Icon
                        name="CheckIcon"
                        size={14}
                        variant="solid"
                        className="text-primary mt-0.5 flex-shrink-0"
                      />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Process Included */}
          <div className="glass-card p-8 rounded-[32px] mb-8">
            <h4 className="font-heading text-xl text-white mb-6 tracking-wider text-center">
              INCLUYE EN TODOS LOS PAQUETES
            </h4>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-white text-sm font-medium mb-1">Descontaminación de Pintura</div>
                  <div className="text-white/60 text-xs">Limpieza profunda y clay bar</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-white text-sm font-medium mb-1">Corrección de Defectos</div>
                  <div className="text-white/60 text-xs">Pulido según nivel de paquete</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-white text-sm font-medium mb-1">Curado Controlado</div>
                  <div className="text-white/60 text-xs">Ambiente profesional certificado</div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary hover:bg-primary/90 text-white px-12 py-5 rounded-full font-heading text-sm tracking-wider transition-all duration-300 hover:scale-105"
            >
              CONSULTAR AHORA
            </a>
            <p className="text-white/50 text-xs mt-4">Cotización personalizada vía WhatsApp</p>
          </div>
        </div>
      </div>
    </section>
  );
}